import clsx from 'clsx'
import dayjs from 'dayjs'
import { Typography } from '../../common/typography'
import classes from './CompareTable.module.css'
import { CompareSubject } from '../../compare/Compare'
import { useFullYearData } from '../../widgets/hooks/useFullYearData'
import React from 'react'
import { FmtColored } from '../../widgets/format'
import { useWidgetContext } from '../../widgets/providers'

function DataRowHeader({
  dataTitles,
  color,
  withBorderRadius,
}: {
  dataTitles: string[]
  color: string
  withBorderRadius?: boolean
}) {
  return (
    <div
      className={clsx(classes.dataTitleContainer, {
        [classes.withBorderRadius]: withBorderRadius,
      })}
      style={{ '--color': color } as React.CSSProperties}
    >
      {dataTitles.map((value, i) => (
        <Typography
          key={i}
          fc={{ light: 'white', dark: 'white' }}
          fs="xs"
          lh="150"
          ta="center"
        >
          {value}
        </Typography>
      ))}
    </div>
  )
}

function DataRow({ dataValues }: { dataValues: React.ReactNode[] }) {
  return (
    <div className={classes.dataRowContainer}>
      {dataValues.map((value, i) => (
        <Typography key={i} fs="xs" lh="150" ff="JetBrains Mono" ta="center">
          {value}
        </Typography>
      ))}
    </div>
  )
}

function DataAttribute({ attribute }: { attribute: string }) {
  return (
    <div className={classes.attributeContainer}>
      <Typography
        fc={{ light: 'black', dark: 'gray-3' }}
        fs="xs"
        lh="150"
        ta="center"
      >
        {attribute}
      </Typography>
    </div>
  )
}

function DataAttributeHeader({ attributeName }: { attributeName: string }) {
  return (
    <div className={classes.attributeContainerHeader}>
      <Typography
        fc={{ light: 'black', dark: 'black' }}
        fs="xs"
        fw={500}
        lh="150"
        ta="center"
      >
        {attributeName}
      </Typography>
    </div>
  )
}

export default function CompareTable({
  subject,
  comparand,
}: {
  subject: CompareSubject
  comparand: CompareSubject
}) {
  const ctx = useWidgetContext()
  const competitionYear = ctx.competition_start
    ? dayjs(ctx.competition_start).year()
    : dayjs().year()

  // Fetch data for subject (first column)
  const subjectData = useFullYearData(
    competitionYear,
    subject?.type || 'player',
    subject?.id || 0,
    ['percent', 'percent'],
    ['value', 'value_ytd']
  )

  // Fetch data for comparand (second column)
  const comparandData = useFullYearData(
    competitionYear,
    comparand?.type || 'player',
    comparand?.id || 0,
    ['percent', 'percent'],
    ['value', 'value_ytd']
  )

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]

  return (
    <div className={classes.container}>
      <div className={classes.column}>
        <DataAttributeHeader attributeName="Month" />
        {months.map((month, idx) => (
          <DataAttribute key={idx} attribute={month} />
        ))}
      </div>

      <div className={clsx(classes.column, classes.columnData)}>
        <DataRowHeader dataTitles={['Actual', 'YTD']} color="#8073ad" />
        {subjectData.map((monthData, idx) => {
          const value = monthData.data.percent?.value
          const valueYtd = monthData.data.percent?.value_ytd
          return (
            <DataRow
              key={idx}
              dataValues={[
                <FmtColored
                  value={value}
                  component="percent"
                  statistic="value"
                  metric={ctx.metric || ''}
                />,
                <FmtColored
                  value={valueYtd}
                  component="percent"
                  statistic="value"
                  metric={ctx.metric || ''}
                />,
              ]}
            />
          )
        })}
      </div>

      <div className={clsx(classes.column, classes.columnData)}>
        <DataRowHeader
          dataTitles={['Actual', 'YTD']}
          color="#b2675e"
          withBorderRadius
        />
        {comparandData.map((monthData, idx) => {
          const value = monthData.data.percent?.value
          const valueYtd = monthData.data.percent?.value_ytd
          return (
            <DataRow
              key={idx}
              dataValues={[
                <FmtColored
                  value={value}
                  component="percent"
                  statistic="value"
                  metric={ctx.metric || ''}
                />,
                <FmtColored
                  value={valueYtd}
                  component="percent"
                  statistic="value"
                  metric={ctx.metric || ''}
                />,
              ]}
            />
          )
        })}
      </div>
    </div>
  )
}
